/** Pure shaping for the stack: which toasts show, and how far to lift above a sticky bar. */
import { KIND_SCREEN_READER_LABEL } from "../../../services/notify";
import type { ToastView } from "../../../services/notify";
import type { ToastStackItem } from "../../molecules/ToastStack";
import { TOAST_VIEWPORT_COPY as COPY } from "./ToastViewport.copy";

/**
 * F08 / Flow-07: a success confirmation ("Chart added to the next free spot · Undo") is the design's
 * dark pill at bottom centre, whatever the caller passed. Errors, warnings and info keep the corner stack.
 */
export const withDesignLook = (toast: ToastView): ToastView =>
  toast.kind === "success" ? { ...toast, appearance: "inverse", placement: "bottom-center" } : toast;

export const toStackItem = (toast: ToastView): ToastStackItem => ({
  id: toast.id,
  kind: toast.kind,
  kindLabel: KIND_SCREEN_READER_LABEL[toast.kind],
  title: toast.title,
  body: toast.body,
  icon: toast.icon,
  progress: toast.progress,
  actionLabel: toast.action?.label ?? null,
  secondaryActionLabel: toast.action ? toast.secondaryAction?.label ?? null : null,
  appearance: toast.appearance,
  dismissLabel: COPY.dismiss(toast.title),
});

/** The newest `max` (all when expanded), oldest first, and how many wait under "+N more". */
export const visibleToasts = (
  toasts: ToastView[],
  max: number,
  expanded: boolean,
): { shown: ToastView[]; hiddenCount: number; isExpanded: boolean } => {
  const isExpanded = expanded && toasts.length > max;
  const shown = isExpanded ? toasts : toasts.slice(-max);
  return { shown, hiddenCount: toasts.length - shown.length, isExpanded };
};

interface Box {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
  height: number;
}

/**
 * Rem to lift the stack so it clears every sticky bar it would overlap (prototype `lift()`).
 * `stack` is where the stack sits unlifted; boxes are viewport rects; 1rem = `remPx`.
 */
export const liftFor = (stack: Box, avoid: Box[], viewportHeight: number, gapPx = 16, remPx = 16): number => {
  const base = viewportHeight - gapPx;
  const top = base - stack.height;
  const need = avoid.reduce((max, box) => {
    if (!box.width || !box.height || box.top >= base || box.bottom <= top) return max;
    if (box.left >= stack.right || box.right <= stack.left) return max;
    return Math.max(max, viewportHeight - box.top + 8 - gapPx);
  }, 0);
  return Math.max(0, Math.round(need)) / remPx;
};

export interface CenteredGroup {
  key: string;
  /** The anchor id, or null for the viewport. */
  anchor: string | null;
  toasts: ToastView[];
}

/**
 * Splits the on-screen toasts: bottom-right ones keep the corner stack; bottom-centre ones group by
 * their anchor, and fall back to the viewport when the anchor is not mounted (`isMounted`).
 */
export const splitByPlacement = (
  toasts: ToastView[],
  isMounted: (anchor: string) => boolean,
): { corner: ToastView[]; centered: CenteredGroup[] } => {
  const corner: ToastView[] = [];
  const groups = new Map<string, CenteredGroup>();
  toasts.forEach((toast) => {
    if (toast.placement !== "bottom-center") {
      corner.push(toast);
      return;
    }
    const anchor = toast.anchor && isMounted(toast.anchor) ? toast.anchor : null;
    const key = anchor ? `anchor:${anchor}` : "viewport";
    const group = groups.get(key) ?? { key, anchor, toasts: [] };
    group.toasts.push(toast);
    groups.set(key, group);
  });
  return { corner, centered: Array.from(groups.values()) };
};
